/**
 * CX Card / Product Slider: arrows, drag scroll, auto-scroll, mobile grid toggle. Self-contained.
 */
(function () {
  // Sections and app templates can include this classic script more than once.
  // Reuse its runtime, but still initialise newly inserted section markup.
  if (typeof window.CX?._runCardSlider === 'function') {
    const parent = document.currentScript?.parentElement;
    window.CX._runCardSlider(parent && parent !== document.head ? parent : document);
    return;
  }

  function queryAll(root, selector) {
    if (!root || typeof root.querySelectorAll !== 'function') return [];
    return Array.from(root.querySelectorAll(selector));
  }
  function querySelfAndAll(root, selector) {
    const out = [];
    if (root instanceof Element && root.matches(selector)) out.push(root);
    out.push(...queryAll(root, selector));
    return out;
  }
  let _reducedMotion = false;
  let _reducedMotionBound = false;
  function ensureReducedMotionCache() {
    if (_reducedMotionBound) return;
    _reducedMotionBound = true;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    _reducedMotion = !!mql.matches;
    const onChange = (e) => { _reducedMotion = !!e.matches; };
    if (typeof mql.addEventListener === 'function') mql.addEventListener('change', onChange);
    else if (typeof mql.addListener === 'function') mql.addListener(onChange);
  }
  function prefersReducedMotion() { return _reducedMotion; }

  const CX_CARD_SLIDER_SECTION_SELECTOR = '[data-cx-card-slider="true"]';
  const CX_CARD_SLIDER_TRACK_SELECTOR = '.custom-container__cards';
  const CX_CARD_SLIDER_ARROW_SELECTOR = '.custom-container__arrow';
  const CX_CARD_SLIDER_CARD_SELECTOR = '.custom-container__card';
  const CX_CARD_SLIDER_ARROW_BOUND_FLAG = 'cxCardSliderBound';
  const CX_CARD_SLIDER_TRACK_BOUND_FLAG = 'cxCardSliderTrackBound';
  const CX_CARD_SLIDER_CLICK_BLOCK_FLAG = 'cxCardSliderBlockClick';
  const CX_CARD_SLIDER_LEAD_OVERLAY_ACTIVE_CLASS = 'cx-product-slider--lead-overlay-active';
  const CX_CARD_SLIDER_MOBILE_GRID_TOGGLE_SELECTOR = '[data-cx-mobile-grid-toggle="true"]';
  const CX_CARD_SLIDER_MOBILE_GRID_TOGGLE_BOUND_FLAG = 'cxMobileGridToggleBound';
  const CX_CARD_SLIDER_MOBILE_GRID_EXPANDED_CLASS = 'cx-mobile-grid--expanded';

  const CX_CARD_SLIDER_AUTO_SCROLL_TIMER = new WeakMap();
  const CX_CARD_SLIDER_TRACK_TILE_DELTA_CACHE = new WeakMap();
  const CX_CARD_SLIDER_TRACK_GAP_CACHE = new WeakMap();
  const CX_CARD_SLIDER_TRACK_RESIZE_OBSERVER = new WeakMap();
  const CX_CARD_SLIDER_INFINITE_SEGMENT_WIDTH = new WeakMap();
  const CX_CARD_SLIDER_TRACK_LAYOUT_RAF = new WeakMap();
  const CX_CARD_SLIDER_VISIBLE_OBSERVER = new WeakMap();
  const CX_CARD_SLIDER_BOUND_TRACKS = new Set();
  let CX_CARD_SLIDER_WINDOW_RESIZE_BOUND = false;
  const CX_CARD_SLIDER_INFINITE_READY = 'data-cx-infinite-ready';
  const CX_CARD_SLIDER_CLONE_MARKER = 'data-cx-infinite-clone';
  /** @param {Element | Document} root */
  function tjcPlusRemoveExcludedSliderCards(root) {
    const scope = root instanceof Element ? root : document;
    const selector = '.cx-product-slider [data-tjc-membership="true"], .rebuy-product-slider [data-tjc-membership="true"]';
    for (const match of queryAll(scope, selector)) {
      const card = match.closest('.custom-container__card, .rebuy-product-block');
      if (card) {
        card.remove();
        continue;
      }
      if (match instanceof Element && match.tagName === 'PRODUCT-CARD') {
        match.remove();
      }
    }
  }

  /** @param {() => void} fn */
  function cxCardSliderRunWhenIdle(fn) {
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(fn, { timeout: 2000 });
    } else {
      requestAnimationFrame(() => requestAnimationFrame(fn));
    }
  }

  /** @param {HTMLElement} el */
  function cxCardSliderGetGapPx(el) {
    const cached = CX_CARD_SLIDER_TRACK_GAP_CACHE.get(el);
    if (typeof cached === 'number') return cached;

    const styles = window.getComputedStyle(el);
    const raw = styles.columnGap || styles.gap || '0';
    const gap = Number.parseFloat(raw);
    const gapPx = Number.isFinite(gap) ? gap : 0;
    CX_CARD_SLIDER_TRACK_GAP_CACHE.set(el, gapPx);
    return gapPx;
  }

  /** @param {HTMLElement} track */
  function cxCardSliderInvalidateTrackMetrics(track) {
    CX_CARD_SLIDER_TRACK_TILE_DELTA_CACHE.delete(track);
    CX_CARD_SLIDER_TRACK_GAP_CACHE.delete(track);
    CX_CARD_SLIDER_INFINITE_SEGMENT_WIDTH.delete(track);
    track.removeAttribute('data-cx-infinite-segment-width');
  }

  /** @param {HTMLElement} track */
  function cxCardSliderMeasureTileDelta(track) {
    const cached = CX_CARD_SLIDER_TRACK_TILE_DELTA_CACHE.get(track);
    if (typeof cached === 'number' && cached > 0) return cached;

    /** @type {HTMLElement | null} */
    const firstCard = /** @type {any} */ (track.querySelector(CX_CARD_SLIDER_CARD_SELECTOR) || track.firstElementChild);
    if (!firstCard) return 0;

    const cardWidth = firstCard.offsetWidth;
    const gap = cxCardSliderGetGapPx(track);
    const delta = cardWidth + gap;
    if (delta > 0) CX_CARD_SLIDER_TRACK_TILE_DELTA_CACHE.set(track, delta);
    return delta;
  }

  /** @param {HTMLElement} track */
  function cxCardSliderScheduleLayoutPass(track, fn) {
    const pending = CX_CARD_SLIDER_TRACK_LAYOUT_RAF.get(track);
    if (pending) cancelAnimationFrame(pending);
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        CX_CARD_SLIDER_TRACK_LAYOUT_RAF.delete(track);
        fn();
      });
    });
    CX_CARD_SLIDER_TRACK_LAYOUT_RAF.set(track, id);
  }

  /** @param {HTMLElement} track */
  function cxCardSliderGetScrollAmountPx(track) {
    return cxCardSliderMeasureTileDelta(track);
  }

  /** @param {HTMLElement} track */
  function cxCardSliderGetScrollDeltaPx(track) {
    const mode = (track.dataset.scrollMode || 'tile').toLowerCase();
    if (mode === 'viewport') return track.clientWidth;
    return cxCardSliderGetScrollAmountPx(track);
  }

  /**
   * @param {HTMLElement} track
   * @returns {number | null} segment width for infinite scroll, or null
   */
  function cxCardSliderGetInfiniteSegmentWidth(track) {
    if (!track.hasAttribute(CX_CARD_SLIDER_INFINITE_READY)) return null;
    const cached = CX_CARD_SLIDER_INFINITE_SEGMENT_WIDTH.get(track);
    if (typeof cached === 'number' && cached > 0) return cached;
    const seg = Number.parseFloat(track.dataset.cxInfiniteSegmentWidth || '0');
    if (Number.isFinite(seg) && seg > 0) return seg;
    return null;
  }

  /**
   * @param {HTMLElement} track
   * @param {'left' | 'right'} [direction]
   */
  function cxCardSliderScrollNext(track, direction = 'right') {
    const delta = cxCardSliderGetScrollDeltaPx(track);
    if (!delta) return;

    const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
    const sign = direction === 'left' ? -1 : 1;
    const segmentWidth = cxCardSliderGetInfiniteSegmentWidth(track);
    const maxScrollLeft = track.scrollWidth - track.clientWidth;
    const atEnd = track.scrollLeft >= maxScrollLeft - 1;
    const atStart = track.scrollLeft <= 1;

    if (segmentWidth != null) {
      if (direction === 'left' && atStart) {
        track.scrollTo({ left: segmentWidth - delta, behavior: 'auto' });
        requestAnimationFrame(() => {
          track.scrollBy({ left: -delta, behavior });
        });
        return;
      }
    } else {
      if (direction !== 'left' && atEnd) return;
      if (direction === 'left' && atStart) return;
    }

    track.scrollBy({ left: sign * delta, behavior });
  }

  /** @param {HTMLElement} track */
  function cxCardSliderComputeEdgeState(track) {
    const segmentWidth = cxCardSliderGetInfiniteSegmentWidth(track);
    const scrollLeft = track.scrollLeft;
    const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
    let atStart = scrollLeft <= 1;
    let atEnd = scrollLeft >= maxScrollLeft - 1;
    if (segmentWidth != null) {
      atEnd = false;
      atStart = false;
    }
    return {
      maxScrollLeft,
      atStart,
      atEnd
    };
  }

  /**
   * When auto-scroll is enabled, duplicate track content so we can loop seamlessly.
   * @param {HTMLElement} track
   */
  function cxCardSliderSetupInfiniteScroll(track) {
    if (track.hasAttribute(CX_CARD_SLIDER_INFINITE_READY)) return;
    const enabled = track.dataset.autoScrollEnabled === 'true';
    if (!enabled) return;

    const cards = queryAll(track, CX_CARD_SLIDER_CARD_SELECTOR);
    if (!cards.length) return;

    // Measure segment width before cloning (avoids layout read after DOM write).
    const gap = cxCardSliderGetGapPx(track);
    let segmentWidth = 0;
    for (const card of cards) {
      if (card instanceof HTMLElement) segmentWidth += card.offsetWidth;
    }
    if (cards.length > 1) segmentWidth += gap * (cards.length - 1);

    const fragment = document.createDocumentFragment();
    for (const card of cards) {
      const clone = card.cloneNode(true);
      if (clone instanceof HTMLElement) clone.setAttribute(CX_CARD_SLIDER_CLONE_MARKER, 'true');
      fragment.appendChild(clone);
    }
    track.appendChild(fragment);

    track.setAttribute(CX_CARD_SLIDER_INFINITE_READY, 'true');

    if (segmentWidth > 0) {
      CX_CARD_SLIDER_INFINITE_SEGMENT_WIDTH.set(track, segmentWidth);
      track.dataset.cxInfiniteSegmentWidth = String(Math.round(segmentWidth));
    }

    if (typeof ResizeObserver === 'function') {
      const ro = new ResizeObserver(() => {
        cxCardSliderScheduleLayoutPass(track, () => {
          cxCardSliderInvalidateTrackMetrics(track);
          const liveCards = queryAll(track, CX_CARD_SLIDER_CARD_SELECTOR).filter(
            (card) => card instanceof HTMLElement && !card.hasAttribute(CX_CARD_SLIDER_CLONE_MARKER)
          );
          if (!liveCards.length) return;
          const gapLive = cxCardSliderGetGapPx(track);
          let seg = 0;
          for (const card of liveCards) {
            if (card instanceof HTMLElement) seg += card.offsetWidth;
          }
          if (liveCards.length > 1) seg += gapLive * (liveCards.length - 1);
          if (seg > 0) {
            CX_CARD_SLIDER_INFINITE_SEGMENT_WIDTH.set(track, seg);
            track.dataset.cxInfiniteSegmentWidth = String(Math.round(seg));
          }
          cxCardSliderMeasureTileDelta(track);
          cxCardSliderUpdateArrowsForTrack(track);
        });
      });
      ro.observe(track);
      CX_CARD_SLIDER_TRACK_RESIZE_OBSERVER.set(track, ro);
    }
  }

  const CX_CARD_SLIDER_ARROWS_WRAPPER_HIDDEN_CLASS = 'custom-container__arrows--hidden';

  /** @param {HTMLElement} track */
  function cxCardSliderUpdateArrowsForTrack(track) {
    const section = track.closest(CX_CARD_SLIDER_SECTION_SELECTOR);
    if (!(section instanceof Element)) return;

    const trackId = track.id;
    if (!trackId) return;

    const { maxScrollLeft, atStart, atEnd } = cxCardSliderComputeEdgeState(track);
    const noScroll = maxScrollLeft <= 1;

    const arrowsWrapper = section.querySelector('.custom-container__arrows');
    if (arrowsWrapper instanceof HTMLElement) {
      arrowsWrapper.classList.toggle(CX_CARD_SLIDER_ARROWS_WRAPPER_HIDDEN_CLASS, noScroll);
    }

    section.querySelectorAll(CX_CARD_SLIDER_ARROW_SELECTOR).forEach((arrow) => {
      if (!(arrow instanceof HTMLButtonElement)) return;
      if (arrow.dataset.target !== trackId) return;

      const dir = arrow.dataset.direction === 'left' ? 'left' : 'right';
      const shouldDisable = noScroll || (dir === 'left' ? atStart : atEnd);
      arrow.disabled = shouldDisable;
    });
  }

  /** @param {Element} arrow */
  function cxCardSliderBindArrow(arrow) {
    if (!(arrow instanceof HTMLElement)) return;
    if (arrow.dataset[CX_CARD_SLIDER_ARROW_BOUND_FLAG] === 'true') return;
    arrow.dataset[CX_CARD_SLIDER_ARROW_BOUND_FLAG] = 'true';

    arrow.addEventListener('click', () => {
      if (arrow instanceof HTMLButtonElement && arrow.disabled) return;
      /** @type {'left' | 'right'} */
      const direction = arrow.dataset.direction === 'left' ? 'left' : 'right';
      const targetId = arrow.dataset.target;

      const trackEl =
        (targetId && document.getElementById(targetId)) ||
        arrow.closest(CX_CARD_SLIDER_SECTION_SELECTOR)?.querySelector(CX_CARD_SLIDER_TRACK_SELECTOR) ||
        arrow.closest('.custom-container')?.querySelector(CX_CARD_SLIDER_TRACK_SELECTOR);

      if (!(trackEl instanceof HTMLElement)) return;
      cxCardSliderScrollNext(trackEl, direction);
      cxCardSliderScheduleLayoutPass(trackEl, () => cxCardSliderUpdateArrowsForTrack(trackEl));
    });
  }

  /** @param {HTMLElement} track */
  function cxCardSliderBindAutoScroll(track) {
    if (!track) return;

    function clear() {
      const existing = CX_CARD_SLIDER_AUTO_SCROLL_TIMER.get(track);
      if (existing) window.clearInterval(existing);
      CX_CARD_SLIDER_AUTO_SCROLL_TIMER.delete(track);
    }

    function start() {
      clear();
      const enabled = track.dataset.autoScrollEnabled === 'true';
      if (!enabled) return;

      const interval = Number.parseInt(track.dataset.autoScrollInterval || '4000', 10);
      const ms = Number.isFinite(interval) && interval >= 500 ? interval : 4000;

      const id = window.setInterval(() => {
        const paused = track.dataset.cxAutoScrollPaused === 'true';
        const interacting = track.classList.contains('is-pointer-down') || track.classList.contains('is-dragging');
        if (paused || interacting) return;
        cxCardSliderScrollNext(track, 'right');
        cxCardSliderScheduleLayoutPass(track, () => cxCardSliderUpdateArrowsForTrack(track));
      }, ms);

      CX_CARD_SLIDER_AUTO_SCROLL_TIMER.set(track, id);
    }

    track.addEventListener('mouseenter', () => {
      track.dataset.cxAutoScrollPaused = 'true';
    });
    track.addEventListener('mouseleave', () => {
      track.dataset.cxAutoScrollPaused = 'false';
    });

    start();
  }

  /** @param {HTMLElement} track */
  function cxCardSliderBindDrag(track) {
    if (!track) return;

    let isPointerDown = false;
    let isDragging = false;
    let startX = 0;
    let startScrollLeft = 0;
    let lastX = 0;
    let lastT = 0;
    let velocity = 0;
    let inertiaRaf = 0;
    /** @type {string} */
    let activePointerType = 'mouse';
    /** @type {number | null} */
    let activePointerId = null;

    function cancelInertia() {
      if (inertiaRaf) cancelAnimationFrame(inertiaRaf);
      inertiaRaf = 0;
    }

    const INTERACTIVE_SELECTOR = [
      'button', 'input', 'textarea', 'select', 'option', 'label', 'summary', 'details',
      '[role="button"]', '[role="link"]',
      'swatches-variant-picker-component', 'overflow-list', 'card-gallery', 'slideshow-component', 'slideshow-controls', 'slideshow-arrows',
      '[data-variant-id]', '[data-option-media-id]', '[data-cx-no-drag]', '[on\\:click]'
    ].join(',');

    /** @param {PointerEvent} e */
    function isInteractivePointerStart(e) {
      const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
      for (const node of path) {
        if (!(node instanceof Element)) continue;
        if (node.matches(INTERACTIVE_SELECTOR)) return true;
      }

      const target = e.target;
      if (!(target instanceof Element)) return false;
      return !!target.closest(INTERACTIVE_SELECTOR);
    }

    function setClickBlockOnce() {
      track.dataset[CX_CARD_SLIDER_CLICK_BLOCK_FLAG] = 'true';
      window.setTimeout(() => {
        track.dataset[CX_CARD_SLIDER_CLICK_BLOCK_FLAG] = 'false';
      }, 0);
    }

    /** @param {MouseEvent} e */
    function onClickCapture(e) {
      if (track.dataset[CX_CARD_SLIDER_CLICK_BLOCK_FLAG] !== 'true') return;
      if (!(e.target instanceof Element)) return;

      if (e.target.closest(INTERACTIVE_SELECTOR)) return;

      const link = e.target.closest('a[href]');
      if (!link) return;

      e.preventDefault();
      e.stopPropagation();
    }

    track.addEventListener('click', onClickCapture, true);

    track.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      if (isInteractivePointerStart(e)) return;

      activePointerType = e.pointerType || 'mouse';
      activePointerId = e.pointerId;

      if (activePointerType === 'touch') return;

      cancelInertia();

      isPointerDown = true;
      isDragging = false;
      startX = e.clientX;
      startScrollLeft = track.scrollLeft;
      lastX = e.clientX;
      lastT = performance.now();
      velocity = 0;

      track.classList.add('is-pointer-down');
    });

    track.addEventListener('pointermove', (e) => {
      if (!isPointerDown) return;

      const dx = e.clientX - startX;
      if (!isDragging && Math.abs(dx) > 6) {
        isDragging = true;
        track.classList.add('is-dragging');
        if (activePointerId != null) {
          track.setPointerCapture?.(activePointerId);
        }
      }

      if (!isDragging) return;

      const now = performance.now();
      const dt = now - lastT || 16;
      const moveX = e.clientX - lastX;
      velocity = moveX / dt;
      lastX = e.clientX;
      lastT = now;

      track.scrollLeft = startScrollLeft - dx;
      setClickBlockOnce();
    });

    /** @param {PointerEvent} e */
    function endPointer(e) {
      if (!isPointerDown) return;
      isPointerDown = false;
      track.classList.remove('is-pointer-down');
      if (activePointerId != null) {
        track.releasePointerCapture?.(activePointerId);
      }
      activePointerId = null;

      if (!isDragging) {
        track.classList.remove('is-dragging');
        return;
      }

      track.classList.remove('is-dragging');

      if (prefersReducedMotion()) return;

      let v = -velocity;
      if (!Number.isFinite(v) || Math.abs(v) < 0.05) return;

      const friction = 0.92;
      let last = performance.now();

      const step = () => {
        const now = performance.now();
        const dt = now - last;
        last = now;

        track.scrollLeft += v * dt;
        v *= friction;

        if (Math.abs(v) < 0.02) {
          inertiaRaf = 0;
          return;
        }
        inertiaRaf = requestAnimationFrame(step);
      };

      inertiaRaf = requestAnimationFrame(step);
    }

    track.addEventListener('pointerup', endPointer);
    track.addEventListener('pointercancel', endPointer);
    track.addEventListener('pointerleave', (e) => {
      if (isPointerDown && e.pointerType === 'mouse') endPointer(e);
    });
  }

  /** @param {HTMLElement} track */
  function cxCardSliderBindTrackWhenReady(track) {
    if (!track || track.dataset[CX_CARD_SLIDER_TRACK_BOUND_FLAG] === 'true') return;

    const start = () => cxCardSliderRunWhenIdle(() => cxCardSliderBindTrack(track));

    if (typeof IntersectionObserver !== 'function') {
      start();
      return;
    }

    if (CX_CARD_SLIDER_VISIBLE_OBSERVER.has(track)) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        io.disconnect();
        CX_CARD_SLIDER_VISIBLE_OBSERVER.delete(track);
        start();
      },
      { rootMargin: '120px 0px' }
    );
    io.observe(track);
    CX_CARD_SLIDER_VISIBLE_OBSERVER.set(track, io);
  }

  /** @param {HTMLElement} track */
  function cxCardSliderBindTrack(track) {
    if (!track || track.dataset[CX_CARD_SLIDER_TRACK_BOUND_FLAG] === 'true') return;
    track.dataset[CX_CARD_SLIDER_TRACK_BOUND_FLAG] = 'true';

    /** @type {HTMLElement | null} */
    const section = /** @type {any} */ (track.closest(CX_CARD_SLIDER_SECTION_SELECTOR));
    const leadOverlayEnabled = section?.dataset?.cxLeadOverlay === 'true';

    function updateLeadOverlayState() {
      if (!leadOverlayEnabled) return;
      if (!(section instanceof HTMLElement)) return;
      section.classList.toggle(CX_CARD_SLIDER_LEAD_OVERLAY_ACTIVE_CLASS, track.scrollLeft > 1);
    }

    CX_CARD_SLIDER_BOUND_TRACKS.add(track);

    cxCardSliderSetupInfiniteScroll(track);

    if (typeof ResizeObserver === 'function' && !CX_CARD_SLIDER_TRACK_RESIZE_OBSERVER.has(track)) {
      const ro = new ResizeObserver(() => {
        cxCardSliderScheduleLayoutPass(track, () => {
          cxCardSliderInvalidateTrackMetrics(track);
          cxCardSliderMeasureTileDelta(track);
          cxCardSliderUpdateArrowsForTrack(track);
        });
      });
      ro.observe(track);
      CX_CARD_SLIDER_TRACK_RESIZE_OBSERVER.set(track, ro);
    } else if (!CX_CARD_SLIDER_WINDOW_RESIZE_BOUND && typeof ResizeObserver !== 'function') {
      CX_CARD_SLIDER_WINDOW_RESIZE_BOUND = true;
      window.addEventListener(
        'resize',
        () => {
          CX_CARD_SLIDER_BOUND_TRACKS.forEach((t) => CX_CARD_SLIDER_TRACK_TILE_DELTA_CACHE.delete(t));
        },
        { passive: true }
      );
    }

    cxCardSliderBindDrag(track);
    cxCardSliderBindAutoScroll(track);

    cxCardSliderScheduleLayoutPass(track, () => {
      cxCardSliderMeasureTileDelta(track);
      cxCardSliderUpdateArrowsForTrack(track);
      updateLeadOverlayState();
    });

    let raf = 0;
    let infiniteJumpInProgress = false;
    track.addEventListener(
      'scroll',
      () => {
        const segmentWidth = cxCardSliderGetInfiniteSegmentWidth(track);
        if (segmentWidth != null && !infiniteJumpInProgress && track.scrollLeft >= segmentWidth - 1) {
          infiniteJumpInProgress = true;
          track.scrollLeft = track.scrollLeft - segmentWidth;
          infiniteJumpInProgress = false;
        }
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          cxCardSliderUpdateArrowsForTrack(track);
          updateLeadOverlayState();
        });
      },
      { passive: true }
    );
  }

  /** @param {Element} btn */
  function cxCardSliderBindMobileGridToggle(btn) {
    if (!(btn instanceof HTMLButtonElement)) return;
    if (btn.dataset[CX_CARD_SLIDER_MOBILE_GRID_TOGGLE_BOUND_FLAG] === 'true') return;
    btn.dataset[CX_CARD_SLIDER_MOBILE_GRID_TOGGLE_BOUND_FLAG] = 'true';

    btn.disabled = false;
    btn.removeAttribute('aria-disabled');

    btn.addEventListener('click', () => {
      const section = btn.closest(CX_CARD_SLIDER_SECTION_SELECTOR);
      if (!(section instanceof HTMLElement)) return;
      const isExpanded = section.classList.contains(CX_CARD_SLIDER_MOBILE_GRID_EXPANDED_CLASS);

      const showMoreText = btn.dataset.showMoreText || 'View more';
      const showLessText = btn.dataset.showLessText || 'View less';
      const track = section.querySelector(CX_CARD_SLIDER_TRACK_SELECTOR);
      const behavior = prefersReducedMotion() ? 'auto' : 'smooth';

      if (!isExpanded) {
        section.classList.add(CX_CARD_SLIDER_MOBILE_GRID_EXPANDED_CLASS);
        btn.textContent = showLessText;
        btn.setAttribute('aria-expanded', 'true');
        return;
      }

      section.classList.remove(CX_CARD_SLIDER_MOBILE_GRID_EXPANDED_CLASS);
      btn.textContent = showMoreText;
      btn.setAttribute('aria-expanded', 'false');

      if (track instanceof HTMLElement) track.scrollTo?.({ left: 0, behavior });

      if (track instanceof HTMLElement) {
        track.scrollIntoView?.({ block: 'start', behavior });
      } else {
        section.scrollIntoView?.({ block: 'start', behavior });
      }
    });
  }

  /** @param {Document | Element} root */
  function initCxCardSlider(root) {
    // Theme Editor performance: skip slider init (drag, arrows, auto-scroll) in design mode
    if (window.Shopify?.designMode) return;
    ensureReducedMotionCache();

    const sections = querySelfAndAll(root, CX_CARD_SLIDER_SECTION_SELECTOR);
    if (!sections.length) return;

    for (const section of sections) {
      // HOME-MOUNT-STEPS (assets/lazy-content-section.js, theme setting perf_cx_lazy_mount_steps): a
      // slider whose lazy row is still putting its cards in over several frames is set up by that row's
      // lazy-content-section:loaded event (the listener at the end of this file), once every card is
      // in; set up now, it would measure part of the row. Rows without data-mount-steps are as before.
      if (section.closest('lazy-content-section[data-mount-steps][aria-busy="true"]')) continue;
      section.querySelectorAll(CX_CARD_SLIDER_ARROW_SELECTOR).forEach(cxCardSliderBindArrow);
      section.querySelectorAll(CX_CARD_SLIDER_MOBILE_GRID_TOGGLE_SELECTOR).forEach(cxCardSliderBindMobileGridToggle);
      /** @type {HTMLElement | null} */
      const track = /** @type {any} */ (section.querySelector(CX_CARD_SLIDER_TRACK_SELECTOR));
      if (track) cxCardSliderBindTrackWhenReady(track);
    }
  }

  /** @param {Element} root */
  function destroyCxCardSlider(root) {
    if (!(root instanceof Element)) return;

    const tracks = root.matches(CX_CARD_SLIDER_TRACK_SELECTOR)
      ? [root]
      : /** @type {Element[]} */ (Array.from(root.querySelectorAll(CX_CARD_SLIDER_TRACK_SELECTOR)));

    for (const track of tracks) {
      if (!(track instanceof HTMLElement)) continue;
      CX_CARD_SLIDER_VISIBLE_OBSERVER.get(track)?.disconnect();
      CX_CARD_SLIDER_VISIBLE_OBSERVER.delete(track);
      const existing = CX_CARD_SLIDER_AUTO_SCROLL_TIMER.get(track);
      if (existing) window.clearInterval(existing);
      CX_CARD_SLIDER_AUTO_SCROLL_TIMER.delete(track);

      CX_CARD_SLIDER_TRACK_TILE_DELTA_CACHE.delete(track);
      CX_CARD_SLIDER_TRACK_GAP_CACHE.delete(track);
      CX_CARD_SLIDER_BOUND_TRACKS.delete(track);

      const layoutRaf = CX_CARD_SLIDER_TRACK_LAYOUT_RAF.get(track);
      if (layoutRaf) cancelAnimationFrame(layoutRaf);
      CX_CARD_SLIDER_TRACK_LAYOUT_RAF.delete(track);

      const ro = CX_CARD_SLIDER_TRACK_RESIZE_OBSERVER.get(track);
      ro?.disconnect?.();
      CX_CARD_SLIDER_TRACK_RESIZE_OBSERVER.delete(track);
    }
  }

  /** @type {any} */ (window)['CX'] = /** @type {any} */ (window)['CX'] || {};
  /** @type {any} */ (window)['CX'].initCardSlider = initCxCardSlider;
  /** @type {any} */ (window)['CX'].destroyCardSlider = destroyCxCardSlider;

  function run(root) {
    const scope = root || document;
    cxCardSliderRunWhenIdle(() => {
      tjcPlusRemoveExcludedSliderCards(scope);
      initCxCardSlider(scope);
    });
  }
  /** @type {any} */ (window)['CX']._runCardSlider = run;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => run(document), { once: true });
  } else {
    run(document);
  }
  document.addEventListener('shopify:section:load', (e) => { run(e?.target instanceof Element ? e.target : document); });
  document.addEventListener('shopify:section:unload', (e) => { if (e?.target instanceof Element) destroyCxCardSlider(e.target); });
  document.addEventListener('shopify:section:reorder', () => run(document));
  document.addEventListener('shopify:block:select', () => run(document));
  document.addEventListener('rebuy.ready', () => run(document));
  // E7: lazy sections no longer re-run this file once it is on the page (assets/lazy-content-section.js),
  // so initialise the sliders in each section as it loads.
  document.addEventListener('lazy-content-section:loaded', (e) => { if (e?.target instanceof Element) run(e.target); });
})();
